import { ApiClientError, ApiTransportError, } from "./index.js";
export class VictorConsoleApi {
    opts;
    constructor(opts) {
        this.opts = opts;
    }
    async request(path, init) {
        const url = `${this.opts.baseUrl}${path}`;
        const fetchImpl = this.opts.fetchFn ?? fetch;
        const headers = new Headers(init?.headers);
        headers.set("Accept", "application/json");
        if (init?.body &&
            typeof init.body === "string" &&
            !headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }
        const token = this.opts.getAuthToken?.();
        if (token)
            headers.set("Authorization", `Bearer ${token}`);
        let response;
        try {
            response = await fetchImpl(url, {
                ...init,
                headers,
                credentials: "include",
            });
        }
        catch (cause) {
            throw new ApiTransportError(`Échec réseau sur ${path}`, cause);
        }
        if (response.status === 204)
            return undefined;
        let body;
        try {
            body = await response.json();
        }
        catch (cause) {
            throw new ApiTransportError(`Réponse non-JSON sur ${path}`, cause);
        }
        const apiBody = body;
        if ("error" in apiBody) {
            throw new ApiClientError(apiBody.error.code, apiBody.error.message, response.status, apiBody.error.details);
        }
        if ("data" in apiBody)
            return apiBody.data;
        throw new ApiClientError("invalid_response", "Réponse API non conforme (ni data ni error).", response.status);
    }
    // ---------------------------------------------------------------------------
    // Auth
    // ---------------------------------------------------------------------------
    auth = {
        login: (input) => this.request("/v1/super-admin/auth/login", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        loginMfa: (input) => this.request("/v1/super-admin/auth/login/mfa", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        refresh: () => this.request("/v1/super-admin/auth/refresh", { method: "POST" }),
        logout: () => this.request("/v1/super-admin/auth/logout", { method: "POST" }),
        me: () => this.request("/v1/super-admin/auth/me"),
        changePassword: (input) => this.request("/v1/super-admin/auth/me/password", {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        mfaSetup: () => this.request("/v1/super-admin/auth/mfa/setup", { method: "POST" }),
        mfaActivate: (input) => this.request("/v1/super-admin/auth/mfa/activate", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        mfaDisable: (input) => this.request("/v1/super-admin/auth/mfa/disable", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    // ---------------------------------------------------------------------------
    // Tenants (cross-tenant)
    // ---------------------------------------------------------------------------
    tenants = {
        list: (query = {}) => {
            const params = new URLSearchParams();
            if (query.statut)
                params.set("statut", query.statut);
            if (query.q)
                params.set("q", query.q);
            const qs = params.toString();
            return this.request(`/v1/super-admin/tenants${qs ? `?${qs}` : ""}`);
        },
        get: (id) => this.request(`/v1/super-admin/tenants/${id}`),
        create: (input) => this.request("/v1/super-admin/tenants", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/super-admin/tenants/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        block: (id, motif) => this.request(`/v1/super-admin/tenants/${id}/block`, {
            method: "POST",
            body: JSON.stringify({ motif }),
        }),
        unblock: (id) => this.request(`/v1/super-admin/tenants/${id}/unblock`, {
            method: "POST",
        }),
        markDemoDone: (id) => this.request(`/v1/super-admin/tenants/${id}/demo-faite`, {
            method: "POST",
        }),
    };
    // ---------------------------------------------------------------------------
    // Analytics (PostHog proxifié — session replay)
    // ---------------------------------------------------------------------------
    analytics = {
        listRecordings: (limit = 50) => this.request(`/v1/super-admin/analytics/recordings?limit=${limit}`),
        recordingEmbed: (id) => this.request(`/v1/super-admin/analytics/recordings/${encodeURIComponent(id)}/embed`),
    };
    // ---------------------------------------------------------------------------
    // Stats
    // ---------------------------------------------------------------------------
    stats = {
        get: () => this.request("/v1/super-admin/stats"),
        business: () => this.request("/v1/super-admin/stats/business"),
    };
    // ---------------------------------------------------------------------------
    // Billing Hubblot
    // ---------------------------------------------------------------------------
    billing = {
        listPacks: () => this.request("/v1/super-admin/billing/packs"),
        getAbonnement: (tenantId) => this.request(`/v1/super-admin/billing/tenants/${tenantId}/abonnement`),
        createAbonnement: (input) => this.request("/v1/super-admin/billing/abonnements", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateAbonnement: (tenantId, input) => this.request(`/v1/super-admin/billing/tenants/${tenantId}/abonnement`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        setExtension: (tenantId, input) => this.request(`/v1/super-admin/billing/tenants/${tenantId}/extension`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        getWallets: (tenantId) => this.request(`/v1/super-admin/billing/tenants/${tenantId}/wallets`),
        recharge: (input) => this.request("/v1/super-admin/billing/recharge", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    // ---------------------------------------------------------------------------
    // Fiche client — Paiements / Documents / Users / License / Backup / Audit
    // ---------------------------------------------------------------------------
    payments = {
        list: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/payments`),
    };
    facturesHubblot = {
        list: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/factures-hubblot`),
        send: (tenantId, id) => this.request(`/v1/super-admin/tenants/${tenantId}/factures-hubblot/${id}/send`, { method: "POST" }),
        pdfUrl: (tenantId, id) => `${this.opts.baseUrl}/v1/super-admin/tenants/${tenantId}/factures-hubblot/${id}/pdf`,
    };
    /** Demandes de prestations Hubblot envoyées par les agences (Boutique). */
    demandesServices = {
        list: () => this.request(`/v1/super-admin/demandes-services`),
        setStatut: (id, statut) => this.request(`/v1/super-admin/demandes-services/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ statut }),
        }),
    };
    /** Centre d'aide : CRUD catégories + articles. */
    help = {
        tree: () => this.request(`/v1/super-admin/help/tree`),
        searchMisses: () => this.request(`/v1/super-admin/help/search-misses`),
        createCategory: (input) => this.request(`/v1/super-admin/help/categories`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateCategory: (id, input) => this.request(`/v1/super-admin/help/categories/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        deleteCategory: (id) => this.request(`/v1/super-admin/help/categories/${id}`, {
            method: "DELETE",
        }),
        createArticle: (input) => this.request(`/v1/super-admin/help/articles`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateArticle: (id, input) => this.request(`/v1/super-admin/help/articles/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        deleteArticle: (id) => this.request(`/v1/super-admin/help/articles/${id}`, { method: "DELETE" }),
    };
    /** Tickets de support (cross-tenant). */
    support = {
        list: (filter) => {
            const p = new URLSearchParams();
            if (filter?.statut)
                p.set("statut", filter.statut);
            if (filter?.priorite)
                p.set("priorite", filter.priorite);
            const qs = p.toString();
            return this.request(`/v1/super-admin/support/tickets${qs ? `?${qs}` : ""}`);
        },
        get: (id) => this.request(`/v1/super-admin/support/tickets/${id}`),
        context: (id) => this.request(`/v1/super-admin/support/tickets/${id}/context`),
        reply: (id, body, attachments) => this.request(`/v1/super-admin/support/tickets/${id}/reply`, {
            method: "POST",
            body: JSON.stringify({ body, attachments }),
        }),
        uploadAttachment: async (ticketId, file) => {
            const fd = new FormData();
            fd.append("file", file);
            const token = this.opts.getAuthToken?.();
            const res = await (this.opts.fetchFn ?? fetch)(`${this.opts.baseUrl}/v1/super-admin/support/tickets/${ticketId}/upload`, {
                method: "POST",
                body: fd,
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok)
                throw new Error("upload_failed");
            return (await res.json()).data;
        },
        downloadAttachment: async (key) => {
            const token = this.opts.getAuthToken?.();
            const res = await (this.opts.fetchFn ?? fetch)(`${this.opts.baseUrl}/v1/super-admin/support/attachment?key=${encodeURIComponent(key)}`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok)
                throw new Error("download_failed");
            return res.blob();
        },
        typing: (id) => this.request(`/v1/super-admin/support/tickets/${id}/typing`, {
            method: "POST",
        }),
        setStatut: (id, statut) => this.request(`/v1/super-admin/support/tickets/${id}/statut`, {
            method: "PATCH",
            body: JSON.stringify({ statut }),
        }),
        setPriorite: (id, priorite) => this.request(`/v1/super-admin/support/tickets/${id}/priorite`, {
            method: "PATCH",
            body: JSON.stringify({ priorite }),
        }),
        macros: () => this.request(`/v1/super-admin/support/macros`),
        createMacro: (input) => this.request(`/v1/super-admin/support/macros`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateMacro: (id, input) => this.request(`/v1/super-admin/support/macros/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        deleteMacro: (id) => this.request(`/v1/super-admin/support/macros/${id}`, {
            method: "DELETE",
        }),
    };
    documents = {
        list: (tenantId, query = {}) => {
            const params = new URLSearchParams();
            if (query.type)
                params.set("type", query.type);
            if (query.q)
                params.set("q", query.q);
            if (query.statut)
                params.set("statut", query.statut);
            if (query.limit != null)
                params.set("limit", String(query.limit));
            if (query.offset != null)
                params.set("offset", String(query.offset));
            const qs = params.toString();
            return this.request(`/v1/super-admin/tenants/${tenantId}/documents${qs ? `?${qs}` : ""}`);
        },
        pdfUrl: (tenantId, type, docId) => `${this.opts.baseUrl}/v1/super-admin/tenants/${tenantId}/documents/${type}/${docId}/pdf`,
    };
    users = {
        list: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/users`),
        resetPassword: (tenantId, userId) => this.request(`/v1/super-admin/tenants/${tenantId}/users/${userId}/reset-password`, { method: "POST" }),
        setStatut: (tenantId, userId, statut) => this.request(`/v1/super-admin/tenants/${tenantId}/users/${userId}/statut`, {
            method: "PATCH",
            body: JSON.stringify({ statut }),
        }),
        impersonate: (tenantId, userId) => this.request(`/v1/super-admin/tenants/${tenantId}/users/${userId}/impersonate`, { method: "POST" }),
    };
    license = {
        get: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/license`),
        addModule: (tenantId, input) => this.request(`/v1/super-admin/tenants/${tenantId}/license/modules`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        revokeModule: (tenantId, moduleCode) => this.request(`/v1/super-admin/tenants/${tenantId}/license/modules/${encodeURIComponent(moduleCode)}`, { method: "DELETE" }),
    };
    backup = {
        pdfsUrl: (tenantId) => `${this.opts.baseUrl}/v1/super-admin/tenants/${tenantId}/backup/pdfs`,
        jsonUrl: (tenantId) => `${this.opts.baseUrl}/v1/super-admin/tenants/${tenantId}/backup/json`,
        restoreJson: (tenantId, input) => this.request(`/v1/super-admin/tenants/${tenantId}/restore/json`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    audit = {
        list: (tenantId, limit = 100) => this.request(`/v1/super-admin/tenants/${tenantId}/audit?limit=${limit}`),
        verify: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/audit/verify`),
    };
    contratsSaas = {
        list: (tenantId) => this.request(`/v1/super-admin/tenants/${tenantId}/contrats`),
        pdfUrl: (tenantId, contratId) => `${this.opts.baseUrl}/v1/super-admin/tenants/${tenantId}/contrats/${contratId}/pdf`,
    };
    legal = {
        list: () => this.request("/v1/super-admin/legal"),
        listVersions: (code) => this.request(`/v1/super-admin/legal/${code}/versions`),
        getCurrent: (code) => this.request(`/v1/super-admin/legal/${code}`),
        getVersion: (code, version) => this.request(`/v1/super-admin/legal/${code}/versions/${encodeURIComponent(version)}`),
        pdfUrl: (code, version) => `${this.opts.baseUrl}/v1/super-admin/legal/${code}/versions/${encodeURIComponent(version)}/pdf`,
        publish: (code, input) => this.request(`/v1/super-admin/legal/${code}`, {
            method: "PUT",
            body: JSON.stringify(input),
        }),
    };
    status = {
        listIncidents: () => this.request("/v1/super-admin/status/incidents"),
        createIncident: (input) => this.request("/v1/super-admin/status/incidents", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateIncident: (id, input) => this.request(`/v1/super-admin/status/incidents/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        summary: () => this.request("/v1/public/status/summary"),
    };
    errors = {
        list: (params) => {
            const q = new URLSearchParams();
            if (params?.statut)
                q.set("statut", params.statut);
            if (params?.source)
                q.set("source", params.source);
            if (params?.limit)
                q.set("limit", String(params.limit));
            if (params?.offset)
                q.set("offset", String(params.offset));
            const qs = q.toString();
            return this.request(`/v1/super-admin/errors${qs ? `?${qs}` : ""}`);
        },
        resolve: (id) => this.request(`/v1/super-admin/errors/${id}/resolve`, {
            method: "PATCH",
        }),
    };
}
export function createVictorConsoleApi(opts) {
    return new VictorConsoleApi(opts);
}
//# sourceMappingURL=console.js.map